import * as React from 'react';

/**
 * Size variants for date picker components
 * - sm: 32px (compact)
 * - md: 40px (default)
 * - lg: 48px (comfortable)
 */
export type DatePickerSize = 'sm' | 'md' | 'lg';

/**
 * Height classes for each size variant
 */
export const sizeHeightClasses: Record<DatePickerSize, string> = {
  sm: 'h-8', // 32px
  md: 'h-10', // 40px
  lg: 'h-12', // 48px
};

/**
 * Handle keyboard events for date picker inputs.
 * Opens the calendar on Enter, Space, or ArrowDown when it's not already open.
 */
export function handleDatePickerKeyDown(
  e: React.KeyboardEvent<HTMLInputElement>,
  isOpen: boolean,
  setIsOpen: (open: boolean) => void
): void {
  if (e.key === 'Enter' || e.key === ' ') {
    if (!isOpen) {
      e.preventDefault();
      setIsOpen(true);
    }
  } else if (e.key === 'ArrowDown' && !isOpen) {
    e.preventDefault();
    setIsOpen(true);
  }
}

/**
 * Format a date according to a format string
 */
export function formatDate(date: Date | null, format: string): string {
  if (!date) return '';

  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear().toString();

  switch (format) {
    case 'dd/MM/yyyy':
      return `${day}/${month}/${year}`;
    case 'yyyy-MM-dd':
      return `${year}-${month}-${day}`;
    case 'MM/dd/yyyy':
    default:
      return `${month}/${day}/${year}`;
  }
}

/**
 * Parse a date string according to a format
 */
export function parseDate(value: string, format: string): Date | null {
  if (!value) return null;

  const parts = value.split(/[/-]/);
  if (parts.length !== 3) return null;

  /* v8 ignore start -- parts[0..2] are always defined after the length === 3 guard above */
  const part0 = parts[0] ?? '';
  const part1 = parts[1] ?? '';
  const part2 = parts[2] ?? '';
  /* v8 ignore stop */

  let year: number, month: number, day: number;

  switch (format) {
    case 'dd/MM/yyyy':
      day = Number.parseInt(part0, 10);
      month = Number.parseInt(part1, 10) - 1;
      year = Number.parseInt(part2, 10);
      break;
    case 'yyyy-MM-dd':
      year = Number.parseInt(part0, 10);
      month = Number.parseInt(part1, 10) - 1;
      day = Number.parseInt(part2, 10);
      break;
    case 'MM/dd/yyyy':
    default:
      month = Number.parseInt(part0, 10) - 1;
      day = Number.parseInt(part1, 10);
      year = Number.parseInt(part2, 10);
      break;
  }

  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null;
  if (month < 0 || month > 11) return null;
  if (day < 1 || day > 31) return null;
  if (year < 1900 || year > 2100) return null;

  const date = new Date(year, month, day);
  // Verify the date is valid (e.g., Feb 30 would become Mar 2)
  if (date.getMonth() !== month) return null;

  return date;
}
